import React from "react";

import Button from "@/components/ui/Button.jsx";

export const REGIONAL_HEALTH_SADC_BOUNDS = [
  [8.0, -35.5],
  [41.5, -3.8],
];

export const REGIONAL_HEALTH_SADC_DEFAULT_VIEW = {
  latitude: -19.5,
  longitude: 25.8,
  zoom: 3.4,
};

export const REGIONAL_HEALTH_COUNTRY_VIEWS = {
  Angola: { bounds: [[10.1, -18.4], [24.3, -4.3]], center: [17.9, -11.2], zoom: 4.2 },
  Botswana: { bounds: [[19.9, -26.95], [29.5, -17.78]], center: [24.7, -22.2], zoom: 5.2 },
  Comoros: { bounds: [[43.1, -12.45], [44.6, -11.15]], center: [43.3, -11.9], zoom: 8.1 },
  "DR Congo": { bounds: [[12.0, -13.6], [31.4, 5.4]], center: [23.6, -2.1], zoom: 3.4 },
  Eswatini: { bounds: [[30.7, -27.35], [32.1, -25.7]], center: [31.5, -26.55], zoom: 7.1 },
  Lesotho: { bounds: [[27.0, -30.7], [29.55, -28.55]], center: [28.25, -29.45], zoom: 7.0 },
  Madagascar: { bounds: [[43.15, -25.6], [50.8, -11.8]], center: [46.7, -19.2], zoom: 4.1 },
  Malawi: { bounds: [[32.65, -17.2], [36.0, -9.3]], center: [34.25, -13.2], zoom: 5.6 },
  Mauritius: { bounds: [[56.4, -20.65], [57.95, -19.95]], center: [57.55, -20.2], zoom: 8.7 },
  Mozambique: { bounds: [[30.15, -27.0], [40.93, -10.45]], center: [35.53, -18.9], zoom: 4.2 },
  Namibia: { bounds: [[11.7, -28.98], [25.3, -16.95]], center: [17.2, -22.4], zoom: 4.5 },
  Seychelles: { bounds: [[55.2, -5.1], [56.05, -3.9]], center: [55.46, -4.62], zoom: 9.4 },
  "South Africa": { bounds: [[16.3, -34.9], [32.9, -22.0]], center: [24.0, -29.0], zoom: 4.2 },
  Tanzania: { bounds: [[29.3, -11.75], [40.7, -0.95]], center: [34.8, -6.2], zoom: 4.6 },
  Zambia: { bounds: [[21.85, -18.1], [33.75, -8.2]], center: [27.9, -13.4], zoom: 4.9 },
  Zimbabwe: { bounds: [[25.2, -22.45], [33.1, -15.5]], center: [29.8, -19.2], zoom: 5.2 },
};

const COUNTRY_I18N_KEYS = {
  Angola: "angola",
  Botswana: "botswana",
  Comoros: "comoros",
  "DR Congo": "drCongo",
  Eswatini: "eswatini",
  Lesotho: "lesotho",
  Madagascar: "madagascar",
  Malawi: "malawi",
  Mauritius: "mauritius",
  Mozambique: "mozambique",
  Namibia: "namibia",
  Seychelles: "seychelles",
  "South Africa": "southAfrica",
  Tanzania: "tanzania",
  Zambia: "zambia",
  Zimbabwe: "zimbabwe",
};

const EXACT_COUNTRY_ALIASES = new Map([
  ["bwa", "Botswana"], ["bw", "Botswana"],
  ["drc", "DR Congo"], ["democratic republic of the congo", "DR Congo"],
  ["swaziland", "Eswatini"], ["sz", "Eswatini"],
  ["ls", "Lesotho"], ["mg", "Madagascar"], ["mw", "Malawi"],
  ["mu", "Mauritius"], ["mz", "Mozambique"], ["na", "Namibia"],
  ["sc", "Seychelles"], ["rsa", "South Africa"], ["za", "South Africa"],
  ["tz", "Tanzania"], ["zm", "Zambia"], ["zw", "Zimbabwe"],
]);

export function resolveRegionalHealthCountry(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const canonical = Object.keys(REGIONAL_HEALTH_COUNTRY_VIEWS).find(
    (country) => country.toLowerCase() === raw.toLowerCase(),
  );
  return canonical || EXACT_COUNTRY_ALIASES.get(raw.toLowerCase()) || "";
}

export function resolveRegionalHealthCountryLabel(t, country) {
  const key = COUNTRY_I18N_KEYS[country];
  return key ? t(`regionalHealth.countries.${key}`, country) : country || "";
}

export function RegionalHealthMapScopeControls({
  activeScope,
  countryLabel,
  countryEnabled = true,
  sadcEnabled = true,
  resetLabel,
  onCountry,
  onSadc,
  onReset,
}) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <Button
        variant={activeScope === "country" ? "tonal" : "ghost"}
        size="sm"
        onClick={onCountry}
        disabled={!countryEnabled}
        className="h-8 rounded-full px-4"
        aria-pressed={activeScope === "country"}
      >
        {countryLabel}
      </Button>
      <Button
        variant={activeScope === "sadc" ? "tonal" : "ghost"}
        size="sm"
        onClick={onSadc}
        disabled={!sadcEnabled}
        className="h-8 rounded-full px-4"
        aria-pressed={activeScope === "sadc"}
      >
        SADC
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onReset}
        className="ml-auto h-8 rounded-full px-3"
      >
        {resetLabel}
      </Button>
    </div>
  );
}

export function RegionalHealthMapLegendRows({ items }) {
  return items.map((item) => (
    <div key={item.key} className="flex h-10 items-center gap-3 px-4">
      <span
        className="h-4 w-4 rounded-full"
        style={{ backgroundColor: item.color }}
        aria-hidden="true"
      />
      <span className="text-body-large text-on-surface">{item.label}</span>
    </div>
  ));
}
